/**
 * The inbox: a bell in the toolbar with the number of open notices, and the
 * notices under it. Notices live in the engine (library/inbox.py), so everyone
 * using the library sees the same ones: songs that need editing, and whatever
 * plugins post (Radio's, for instance).
 */

import { useEffect, useRef, useState } from "react";
import { useApp } from "../core/app";
import { dismiss, useInbox } from "../core/needsEdit";
import { Icon } from "./Icons";

function ago(stamp: string): string {
  const then = new Date(stamp).getTime();
  if (!Number.isFinite(then)) return "";
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(stamp).toLocaleDateString();
}

export function InboxButton() {
  const app = useApp();
  const notices = useInbox();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("mousedown", away);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("mousedown", away);
      window.removeEventListener("keydown", esc);
    };
  }, [open]);

  if (app.offline) return null;
  const count = notices.length;
  return (
    <div className="nv-inbox" ref={box}>
      <button
        className={`nv-iconbtn ${open ? "on" : ""}`}
        title={count ? `Inbox: ${count} notice${count === 1 ? "" : "s"}` : "Inbox: nothing new"}
        aria-label={`Inbox, ${count} notice${count === 1 ? "" : "s"}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="bell" />
        {count > 0 && <span className="nv-inbox-count">{count > 99 ? "99+" : count}</span>}
      </button>
      {open && (
        <div className="nv-inbox-panel" role="dialog" aria-label="Inbox">
          <header>Inbox</header>
          {count === 0 && <p className="nv-dim nv-inbox-empty">Nothing needs attention.</p>}
          <ul>
            {notices.map((n) => (
              <li key={n.id}>
                <button
                  className="nv-inbox-item"
                  disabled={!n.view}
                  onClick={() => {
                    if (!n.view) return;
                    // Settings is a dialog, not a screen: "settings" opens it at a page.
                    if (n.view === "settings") app.openSettings(n.params?.page ?? "general");
                    else app.navigate(n.view, n.params ?? undefined);
                    setOpen(false);
                  }}
                >
                  <b>{n.title}</b>
                  {n.body && <span>{n.body}</span>}
                  <small>{n.source === "core" ? "" : `${n.source} · `}{ago(n.updatedAt)}</small>
                </button>
                <button className="nv-iconbtn sm" title="Dismiss" aria-label={`Dismiss “${n.title}”`} onClick={() => void dismiss(n.id)}>
                  <Icon name="close" size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
